import type { ComponentProps, ReactNode } from 'react';
import { FOCUS_RING } from '@/components/console-ui';
import { cn } from 'helpers/cn';
import { GlowHalo, LogoBars } from './LogoBars';

type AiToggleProps = Omit<ComponentProps<'button'>, 'type' | 'children' | 'aria-pressed'> & {
  children: ReactNode;
  /** The panel it opens is open: `aria-pressed`, drawn as the Ink ring (ai-chat.css). */
  pressed: boolean;
  /** AI work it stands for is running: the glow on its fill and the bars bounce. It stays usable. */
  busy?: boolean;
};

/**
 * The Ink button (AiButton's look) for a control that opens and closes an AI panel: the editor's
 * top bar "AI" and the dashboard's "Ask" once its panel is open. Unlike AiButton it is never
 * disabled while the work runs, so the panel can be closed and reopened mid-answer, and progress
 * shows on it with the panel closed. Other props (an id, a label, a tooltip trigger's handlers and
 * ref) go to the button.
 */
export const AiToggle = ({ children, pressed, busy = false, className, ...props }: AiToggleProps) => (
  <button
    {...props}
    type="button"
    aria-pressed={pressed}
    aria-busy={busy || undefined}
    className={cn('ai-trigger ai-ink text-meta font-semibold', busy && 'ai-glow', FOCUS_RING, className)}
  >
    {busy && <GlowHalo />}
    {/* The bounce while working, whatever the step: an Ink button has one working motion. */}
    <LogoBars size={12} state={busy ? 'thinking' : 'idle'} className="on-dark" />
    {children}
  </button>
);
